// src/web/client/src/pages/Platform.jsx
//
// The Ecomoria Platform ("brain") inside this app — Platform tier only.
//
// The shell. It proves the whole chain end to end for the signed-in shop —
// session token → /api/platform (requireFeature 'platform_access') →
// server-to-server bridge → Platform /api/me — and renders exactly ONE honest
// state, decided by the HTTP status alone (resolvePlatformState below):
//
//   200 + a shop in the body ....... connected (the Platform's name for the store)
//   404 + code unknown_shop ........ the Platform has no tenant for this store yet
//   403 ............................ the plan does not include the Platform
//   anything else .................. unavailable (502/503/500, a 200 without a
//                                    shop, a network error, a non-JSON body)
//
// WHY authenticatedFetch AND NOT safeApi: safeApi.get returns `null` for every
// non-2xx response, so a 404 unknown_shop and a 502 both looked identical to
// an empty success — and this page read that `null` as "connected", showing a
// store with no Platform tenant (or a Platform that is down) as connected with
// a blank name. The status is the whole signal here, so it is read directly.
//
// Connected → the children (one lazily-loaded Polaris screen from
// platform/PlatformScreens.jsx), with no header or frame of the gate's own:
// since the six-page menu every page carries its own title. Polaris and its
// stylesheet reach only a merchant who is actually connected.

import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { authenticatedFetch } from '@/utils/authenticatedFetch';
import { getShopDomain } from '@/utils/shopContext';
import { platformUrl } from '@/platform/api';

// One lazy chunk for every Platform screen; each route / tab picks its export.
const fromScreens = (name) => lazy(() => import('@/platform/PlatformScreens.jsx').then((m) => ({ default: m[name] })));
export const PLATFORM_PAGES = {
  homeBrain: fromScreens('HomeBrainPage'),
  customers: fromScreens('CustomersListPage'),
  lifecycle: fromScreens('LifecyclePage'),
  restockCard: fromScreens('RestockReminderCardPage'),
  loyaltyCard: fromScreens('LoyaltyCardPage'),
  replenishReminders: fromScreens('ReplenishRemindersPage'),
  replenishProducts: fromScreens('ReplenishProductsPage'),
  reviews: fromScreens('ReviewsPage'),
  revenue: fromScreens('RevenuePage'),
  customer: fromScreens('Customer360Page'),
};

// PURE: map one /api/platform/me outcome to the page state. `status` is the
// HTTP status (or null when the request never completed); `body` is the parsed
// JSON (or null when it was not JSON). Exported for the tests.
export function resolvePlatformState(status, body) {
  if (status === 200) {
    const shopDomain = body && typeof body.shopDomain === 'string' ? body.shopDomain : '';
    if (shopDomain) return { status: 'connected', name: (body.name && String(body.name)) || shopDomain, shopDomain };
    return { status: 'unavailable' };
  }
  if (status === 404 && body && body.code === 'unknown_shop') return { status: 'not_connected' };
  if (status === 403) return { status: 'locked' };
  return { status: 'unavailable' };
}

// Every Platform route renders through this: it confirms the store is connected
// (/api/platform/me) and renders exactly one honest state; `children` — a lazy
// Platform screen — mounts only in the connected state.
export function PlatformGate({ children }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading' });
  const shopDomain = getShopDomain();

  useEffect(() => {
    let cancelled = false;
    if (!shopDomain) {
      setState({ status: 'no_shop' });
      return undefined;
    }
    (async () => {
      let status = null;
      let body = null;
      try {
        const res = await authenticatedFetch(platformUrl('/me'));
        status = res.status;
        try { body = await res.json(); } catch { body = null; }
      } catch {
        // Network failure, or authenticatedFetch's 402 throw — either way the
        // Platform could not be read. (A 402 also raises the global
        // subscription overlay on its own.)
        status = null;
      }
      if (!cancelled) setState(resolvePlatformState(status, body));
    })();
    return () => { cancelled = true; };
  }, [shopDomain]);

  // No header, no frame, no "Connected · <shop>" line (UX brief, Phase B): the
  // page this gate sits on has its own title, and a connected store simply
  // sees the screen. Only the honest NON-connected states render a card.
  if (state.status === 'connected') {
    return (
      <Suspense
        fallback={(
          <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        )}
      >
        {children}
      </Suspense>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-gate">
      {state.status === 'loading' && (
        <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Connecting…
        </div>
      )}

      {state.status === 'no_shop' && (
        <Card className="border-red-200">
          <CardContent className="py-8 text-center text-gray-700">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-red-500" />
            We couldn't identify your store. Please reopen the app from your Shopify admin.
          </CardContent>
        </Card>
      )}

      {state.status === 'locked' && (
        <Card className="border-indigo-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Platform plan required</CardTitle>
            <CardDescription>This part of Ecomoria is included in the Platform plan.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/billing')}>View plans</Button>
          </CardContent>
        </Card>
      )}

      {state.status === 'not_connected' && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="py-8 text-center text-gray-800">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-yellow-600" />
            This part of Ecomoria hasn't been connected to <strong>{shopDomain}</strong> yet. We're setting it up — check back shortly.
          </CardContent>
        </Card>
      )}

      {state.status === 'unavailable' && (
        <Card className="border-red-200">
          <CardContent className="py-8 text-center text-gray-700">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-red-500" />
            This part of Ecomoria is unavailable right now. Please try again in a few minutes.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
